/* eslint-disable camelcase */

import { Button, Icon, IconType, PageHeader } from '@lmc/ui-kit'
import ExternalLink from 'components/icons/ExternalLink'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { find } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { connect } from 'react-redux'
import { Link, Outlet } from 'react-router-dom'
import { Banner, HelpLink, Spinner } from '../../../../../components/generic'
import PdfExport from '../../../../../components/pdf/PdfExport'
import Operations from '../../../../../operations'
import Selectors from '../../../../../selectors'
import { history } from '../../../../../store'
import { withRouter } from '../../../../../withRouter'

const renderHeader = (props) => {
  const {
    medication,
    router: {
      params: { id, medication_id }
    },
    isPDF,
    resident,
    testId,
    user
  } = props

  const { name } = medication

  let backLink = `/residents/${id}/emar`
  // TODO: there are better ways to do this
  const screen =
    window.location && window.location.pathname.split('/').splice(-1)[0]
  const isDetailsScreen = screen === 'details' || screen === 'edit'
  if (isDetailsScreen) backLink += `/${medication_id}/chart`

  const pdfUrl = encodeURIComponent(
    `residents/${id}/pdf/emar/${medication_id}/chart?printpdf=1`
  )
  const url = `/api/services/pdf/export?url=${pdfUrl}`
  const pdfTitle = `${resident?.preferredName || resident.name.first} ${
    resident.name.last
  } - ${medication.medication.name}`

  return (
    <div
      id="test"
      style={isPDF ? styles.pdfPadding : {}}
      data-testid={
        testId ? `${testId}-resident-med-header` : 'resident-med-header'
      }
    >
      <PageHeader
        onBack={history.back}
        title={name}
        gradient={false}
        testId={
          testId
            ? `${testId}-resident-med-page-header`
            : 'resident-med-page-header'
        }
      >
        <div
          style={styles.buttonsContainer}
          data-testid={
            testId ? `${testId}-resident-med-buttons` : 'resident-med-buttons'
          }
        >
          {!isDetailsScreen && !medication.deleted && (
            <div
              style={styles.headerButtonContainer}
              data-testid={
                testId
                  ? `${testId}-header-button-container`
                  : 'header-button-container'
              }
            >
              <Link
                to="https://support.logmycare.co.uk/en/collections/2749236-emar"
                target="_blank"
                rel="noreferrer"
                style={styles.readMoreLink}
                data-testid={
                  testId ? `${testId}-read-more-link` : 'read-more-link'
                }
              >
                Read more <ExternalLink />
              </Link>

              <Button
                variant="default"
                icon={<Icon customIcon={IconType.Pencil} />}
                onClick={() => {
                  history.push(`/residents/${id}/emar/${medication_id}/edit`)
                }}
                data-testid={
                  testId ? `${testId}-configuration-btn` : 'configuration-btn'
                }
                disabledByPermission={!user?.medicationPermission}
                permissionDisabledText="You don't have permission to configure medications."
                withDisabledTooltipIcon={false}
              >
                Configuration
              </Button>
            </div>
          )}
          {!isPDF && url && (
            <PdfExport
              pdfTitle={pdfTitle}
              url={url}
              text={isDesktop ? 'Download PDF' : 'PDF'}
              style={{ whiteSpace: 'nowrap' }}
              data-testid={
                testId ? `${testId}-download-pdf-btn` : 'download-pdf-btn'
              }
            />
          )}
        </div>
      </PageHeader>
    </div>
  )
}

export function ResidentMedScreen(props) {
  const {
    emarRoundTimings,
    fetchData,
    medication,
    resident,
    user,
    router: { params },
    testId
  } = props
  const flags = useFeatureFlags({ flagNames: ['emar_v2_chart'] })?.data
  const emarV2ChartEnabled = flags?.emar_v2_chart?.enabled ?? false

  let headerRef = null
  const setHeaderRef = (node) => {
    headerRef = node
  }

  // const [startDate, setStartDate] = React.useState(dayjs().subtract(14, 'days'))
  const startDate = dayjs().subtract(14, 'days')
  // const [endDate, setEndDate] = React.useState(dayjs())
  const endDate = dayjs()

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  React.useEffect(() => {
    fetchData({
      medicationId: params.medication_id,
      residentId: params.id,
      startDate,
      endDate,
      emarV2ChartEnabled
    })
    if (headerRef) {
      headerRef?.scrollIntoView(true)
      window.scrollTo(0, 0)
    }
  }, [emarV2ChartEnabled])

  if (!emarRoundTimings || !medication || !resident || !user) return <Spinner />

  const isPDF =
    (window.location &&
      window.location.query &&
      window.location.query.printpdf) ||
    window.location.pathname.includes('pdf')

  return (
    <div
      ref={setHeaderRef}
      style={styles.wrapper}
      data-testid={
        testId ? `${testId}-resident-med-screen` : 'resident-med-screen'
      }
    >
      {renderHeader({ ...props, isPDF })}
      {medication.deleted && (
        <Banner
          text="This medication has been discontinued."
          data-testid={
            testId ? `${testId}-discontinued-banner` : 'discontinued-banner'
          }
        />
      )}
      <div style={isPDF ? styles.pdfPadding : {}}>
        <Outlet />
      </div>
      {/* <div style={styles.helpLinkContainer}>
        <HelpLink supportLink="https://support.logmycare.co.uk/en/collections/2749236-emar" />
      </div> */}
    </div>
  )
}

ResidentMedScreen.propTypes = {
  emarRoundTimings: PropTypes.array.isRequired,
  fetchData: PropTypes.func.isRequired,
  medication: PropTypes.object.isRequired,
  resident: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  testId: PropTypes.string
}

const styles = {
  headerButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: 8
  },
  readMoreLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginRight: 8
  },
  helpLinkContainer: {
    marginTop: 24
  },
  pdfFormButtonContainer: {
    marginRight: 10
  },
  pdfWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 1190,
    minHeight: 1480,
    zIndex: 1000,
    background: 'white',
    padding: '24px 28px'
  },
  pdfPadding: { padding: '0px 24px' },
  wrapper: {
    padding: '24px 0px 48px',
    margin: '0px auto',
    maxWidth: 1080
  },
  buttonsContainer: {
    display: 'flex'
  }
}

const isDesktop = isBrowser || isTablet

const mapStateToProps = (state, props) => ({
  emarRoundTimings: state.data['emar-round-timings'],
  medication: find(Selectors.getResidentMarChart(state, props), {
    id: props.router.params.medication_id
  }),
  resident: Selectors.getResidentProfile(state, { id: props.router.params.id }),
  user: Selectors.getCurrentUserProfile(state)
})

const mapDispatchToProps = (dispatch) => ({
  fetchData: ({
    medicationId,
    residentId,
    startDate,
    endDate,
    emarV2ChartEnabled
  }) => {
    if (emarV2ChartEnabled) {
      dispatch(
        Operations.loadList(
          'emar-round-timings',
          undefined,
          true,
          '/emar/legacy/emar-round-timings'
        )
      )
    } else {
      dispatch(Operations.loadList('emar-round-timings'))
    }
    dispatch(
      Operations.loadListItem(
        'emar-resident-medications',
        medicationId,
        undefined,
        emarV2ChartEnabled
          ? `/emar/medication-schedule-patterns/legacy/emar-resident-medications/${medicationId}`
          : undefined
      )
    )
    dispatch(
      Operations.loadList(
        'emar-rounds',
        {
          resident: residentId,
          from: dayjs(startDate).startOf('day').toISOString(),
          to: dayjs(endDate).endOf('day').toISOString()
        },
        true,
        emarV2ChartEnabled
          ? '/emar/medication-administration-instances/legacy/emar-rounds'
          : undefined
      )
    )
  }
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ResidentMedScreen)
)
